/**
 * Authentication API Service
 * Handles all authentication requests to the backend API
 */

import { getApiBaseUrl } from '../config/apiBaseUrl';

class AuthApiService {
  async makeRequest(endpoint, options = {}) {
    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}${endpoint}`;
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    console.log('API Request:', {
      url: url,
      method: options.method || 'GET',
      baseUrl,
      endpoint: endpoint
    });

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server returned non-JSON response: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        const error = new Error(data.error || `HTTP error! status: ${response.status}`);
        error.code = data.code;
        error.statusCode = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      // Re-throw if it's already our custom error
      if (error.code) {
        throw error;
      }
      
      // Provide more helpful error messages for network errors
      console.error('API Request Error:', {
        url: url,
        error: error.message,
        type: error.name
      });
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error(`Unable to connect to the server. Please check if ${baseUrl} is accessible.`);
      }
      
      // Otherwise, wrap network errors
      throw new Error(error.message || 'Network error occurred');
    }
  }

  // Validate Device (before signup)
  async validateDevice(deviceId, deviceCode) {
    return this.makeRequest('/api/auth/validate-device', {
      method: 'POST',
      body: JSON.stringify({ deviceId, deviceCode })
    });
  }

  // Get Fortunes (Orders) for the logged-in user
  async getFortunes(accessToken, idToken) {
    return this.makeRequest('/api/fortunes', {
      method: 'POST',
      body: JSON.stringify({ accessToken, idToken })
    });
  }

  // Sign Up
  async signUp(username, password, email, firstName, lastName, address, deviceId, deviceCode, storeName, storeAddress, storeCity, storeCountry, storeState, storeZip, storePhone) {
    const payload = {
      username,
      password,
      email,
      firstName,
      lastName,
      address,
      deviceId,
      deviceCode,
      storeName,
      storeAddress,
      storeCity,
      storeCountry,
      storeState,
      storeZip,
      storePhone
    };
    
    console.log('SignUp API call with payload:', {
      username: username ? 'present' : 'missing',
      password: password ? 'present' : 'missing',
      email: email ? 'present' : 'missing',
      firstName: firstName ? 'present' : 'missing',
      lastName: lastName ? 'present' : 'missing',
      address: address ? 'present' : 'missing',
      deviceId: deviceId ? 'present' : 'missing',
      deviceCode: deviceCode ? 'present' : 'missing'
    });
    
    return this.makeRequest('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // Update User Attributes (with access token)
  async updateUserAttributes(accessToken, attributes) {
    return this.makeRequest('/api/auth/update-attributes', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ attributes })
    });
  }

  // Update User Attributes by Username (admin API, for unverified users)
  async updateUserAttributesByUsername(username, attributes) {
    return this.makeRequest('/api/auth/update-attributes-by-username', {
      method: 'POST',
      body: JSON.stringify({ username, attributes })
    });
  }

  // Verify Email
  async confirmSignUp(username, code) {
    return this.makeRequest('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ username, code })
    });
  }

  // Resend Verification Code
  async resendSignUpCode(username) {
    return this.makeRequest('/api/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ username })
    });
  }

  // Sign In
  async signIn(username, password) {
    return this.makeRequest('/api/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
  }

  // Forgot Password
  async forgotPassword(username) {
    return this.makeRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ username })
    });
  }

  // Reset Password
  async resetPassword(username, code, newPassword) {
    return this.makeRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ username, code, newPassword })
    });
  }

  // Change password (requires current password)
  async changePassword(accessToken, currentPassword, newPassword) {
    return this.makeRequest('/api/auth/change-password', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // Refresh Token
  async refreshToken(refreshToken, username) {
    return this.makeRequest('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken, username })
    });
  }

  // Verify Token
  async verifyToken(accessToken) {
    return this.makeRequest('/api/auth/verify-token', {
      method: 'POST',
      body: JSON.stringify({ accessToken })
    });
  }
}

const authApiService = new AuthApiService();
export default authApiService;

